import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  selectServiceMenuData,
  selectServiceMenuDraggingItemId,
  selectServiceMenuSelectedDroppableId,
  selectServiceMenuSelectedItemIdMap,
  selectServiceMenuSelectedItemIds,
  serviceMenuActions,
} from 'features/ServiceMenu/serviceMenuSlice';
import React, { useContext } from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PRIMARY_BUTTON } from '../../../../utils';
import { ServiceMenuListItem } from '../../models';
import { wasMultiSelectKeyUsed, wasToggleInSelectionGroupKeyUsed } from '../../utils/dragutils/utils';
import { COLUMN_WIDTH, ServiceMenuContext } from '../ServiceMenuListTable';

export interface ServiceMenuRowProps {
  rowData: ServiceMenuListItem;
  rowIndex: number;
}

function ServiceMenuRow({ rowData, rowIndex }: ServiceMenuRowProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // selector
  const data = useAppSelector(selectServiceMenuData);
  const selectedDroppableId = useAppSelector(selectServiceMenuSelectedDroppableId);
  const selectedItemIds = useAppSelector(selectServiceMenuSelectedItemIds);
  const selectedItemIdMap = useAppSelector(selectServiceMenuSelectedItemIdMap);
  const draggingItemId = useAppSelector(selectServiceMenuDraggingItemId);

  const serviceMenuContext = useContext(ServiceMenuContext);
  const onRestoreServiceMenu = serviceMenuContext.onRestoreServiceMenu;
  const onSelectedRowChanged = serviceMenuContext.onSelectedRowChanged;

  const draggableId = `${rowData.id}`;
  const droppableId = `root`;

  const isSelected: boolean = droppableId === selectedDroppableId && Boolean(selectedItemIdMap[draggableId]);
  const isGhosting: boolean = isSelected && Boolean(draggingItemId) && draggingItemId !== draggableId;
  const selectionCount = selectedItemIds.length;

  function onKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) {
    if (event.defaultPrevented) {
      return;
    }

    if (snapshot.isDragging) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    // using the event for selection
    event.preventDefault();

    performAction(event);
  }

  // Using onClick as it will be correctly
  // preventing if there was a drag
  function onClick(event: React.MouseEvent<HTMLDivElement>) {
    if (event.defaultPrevented) {
      return;
    }

    if (event.button !== PRIMARY_BUTTON) {
      return;
    }

    // marking the event as used
    const targetElement = event.target as HTMLElement;
    if (!targetElement.classList.contains('form-check-input')) {
      event.preventDefault();
    }

    // (event.currentTarget as HTMLDivElement).focus();
    performAction(event);
  }

  function onTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    if (event.defaultPrevented) {
      return;
    }

    // marking the event as used
    // we would also need to add some extra logic to prevent the click
    // if this element was an checkbox
    const targetElement = event.target as HTMLElement;
    if (!targetElement.classList.contains('form-check-input') && !targetElement.classList.contains('dan-btn-expand')) {
      event.preventDefault();
    }

    // props.toggleSelectionInGroup(props.task.id);
    dispatch(serviceMenuActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: draggableId }));
  }

  function performAction(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
    // const { task, toggleSelection, toggleSelectionInGroup, multiSelectTo } = props;
    if (wasToggleInSelectionGroupKeyUsed(event)) {
      dispatch(serviceMenuActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: draggableId }));
      return;
    }

    if (wasMultiSelectKeyUsed(event)) {
      // multiSelectTo(task.id);
      dispatch(
        serviceMenuActions.multiSelectTo({
          arg: {
            data,
            selectedItemIds,
            droppableId: droppableId,
          },
          newItemId: draggableId,
        })
      );
      return;
    }

    dispatch(serviceMenuActions.toggleSelection({ selectedDroppableId: droppableId, itemId: draggableId }));
  }

  return (
    <Draggable draggableId={`${draggableId}`} index={rowIndex}>
      {(provided, snapshot) => {
        const shouldShowSelection: boolean = snapshot.isDragging && selectionCount > 1;
        const style = serviceMenuContext.getItemStyle(
          snapshot.isDragging,
          provided.draggableProps.style,
          isSelected,
          isGhosting
        );
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...style,
            }}
            onClick={onClick}
            onTouchEnd={onTouchEnd}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => onKeyDown(event, provided, snapshot)}
          >
            <div className="dan-tr">
              <div
                className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                style={{
                  width: `${COLUMN_WIDTH[0]}%`,
                  backgroundColor: rowData.temp_delete_flg ? 'rgb(242, 242, 242)' : style.background,
                }}
              >
                {rowData.delete_flg && (
                  <Button className="btn--orange" onClick={onRestoreServiceMenu?.bind(null, Number(rowData.id))}>
                    {t('ServiceMenuListPage.restore')}
                  </Button>
                )}
                {!rowData.delete_flg && (
                  <Form.Check
                    className="chk-servicemenu-item custom-checkbox"
                    data-type="servicemenu"
                    data-servicemenu={rowData.name}
                    value={rowData.id}
                    onChange={onSelectedRowChanged}
                  ></Form.Check>
                )}
              </div>
              <div
                className="dan-td flex-grow-0 flex-shrink-0 d-flex justify-content-center"
                style={{
                  width: `${COLUMN_WIDTH[1]}%`,
                  backgroundColor: rowData.temp_delete_flg ? 'rgb(242, 242, 242)' : style.background,
                }}
              >
                <span>{rowData.cd}</span>
              </div>
              <div
                className="dan-td flex-grow-0 flex-shrink-0 d-flex justify-content-center"
                style={{
                  width: `${COLUMN_WIDTH[2]}%`,
                  backgroundColor: rowData.temp_delete_flg ? 'rgb(242, 242, 242)' : style.background,
                }}
              >
                <span>{rowData.name}</span>
              </div>

              {shouldShowSelection ? <div className="selection-count">{selectionCount}</div> : null}
            </div>
          </div>
        );
      }}
    </Draggable>
  );
}

export default ServiceMenuRow;
